import { useState, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { pullFromServer } from "../lib/sync";
import { FRANCHISE_NAME, APP_NAME } from "../lib/branding";
import { Lock, EnvelopeSimple, ArrowRight, WifiSlash, Calculator, Kanban, Clock, CalendarBlank, ChartLineUp } from "@phosphor-icons/react";

const DEMO_USERS = [
    { email: "admin@romengine.app", password: "Admin123!", role: "Admin" },
    { email: "manager@romengine.app", password: "Manager123!", role: "Manager" },
    { email: "estimator@romengine.app", password: "Estimator123!", role: "Estimator" },
];

export default function Login() {
    const { user, login, online } = useAuth();
    const navigate = useNavigate();
    // Always land on the Command Center after sign-in — never redirect back
    // to whatever protected page a stale/expired session happened to be on.
    const dest = "/dashboard";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    // AuthContext sets `user` synchronously the instant login() resolves —
    // well before submit()'s own pull/retry/flag/navigate sequence below
    // finishes. Without this guard, the render-time redirect on the next
    // line fires early (a race), mounting Layout before the offline-ready
    // flag is ever set and making the banner's appearance non-deterministic.
    const postLoginRef = useRef(false);

    if (user && !postLoginRef.current) return <Navigate to={dest} replace />;

    async function submit(e) {
        e?.preventDefault();
        postLoginRef.current = true;
        setBusy(true);
        setErr("");
        const r = await login(email, password);
        setBusy(false);
        if (!r.ok) {
            postLoginRef.current = false;
            setErr(r.error || "Login failed");
            return;
        }
        // Warm the local cache for instant offline access. Retry once on a
        // flaky first attempt (short backoff) — an unwarmed cache must never
        // be silently reported as "offline-ready".
        let pulled = await pullFromServer();
        if (!pulled.ok) {
            await new Promise((res) => setTimeout(res, 1500));
            pulled = await pullFromServer();
        }
        // Only mark this device as offline-ready if the pull actually succeeded.
        if (pulled.ok) {
            try { sessionStorage.setItem("rom_just_signed_in", "1"); } catch {}
        }
        navigate(dest);
    }

    function fill(u) {
        setEmail(u.email);
        setPassword(u.password);
    }

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
            {/* Left — hero panel */}
            <div className="relative hidden lg:block bg-[var(--rom-navy)] overflow-hidden">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: "url(/login-hero.jpg)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[var(--rom-navy)]/30 via-[var(--rom-navy)]/55 to-[var(--rom-navy)]/85" />
                <div className="relative h-full flex flex-col p-12 text-white">
                    <div className="flex items-center gap-3">
                        <img
                            src="/servpro-logo.png"
                            alt="SERVPRO"
                            className="w-12 h-12 object-contain"
                            draggable={false}
                        />
                        <div>
                            <div className="font-display font-black text-lg">{APP_NAME}</div>
                            <div className="text-[11px] tracking-[0.18em] uppercase text-white/80">
                                {FRANCHISE_NAME}
                            </div>
                        </div>
                    </div>

                    <div className="mt-auto space-y-6">
                        <div className="font-display font-black text-5xl leading-[1.05]">
                            Quote it. Run it.
                            <br />
                            <span className="text-[var(--rom-orange)]">Get paid.</span>
                        </div>
                        <p className="text-white/85 max-w-md leading-relaxed">
                            The whole restoration job in one workflow — sq-ft ROM &amp; T&amp;M estimating,
                            a project pipeline, timesheets, schedules and live job P&amp;L.
                            Built for the field. Works in airplane mode.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-md pt-2">
                            <Feature icon={Calculator} text="ROM & T&M estimating" />
                            <Feature icon={Kanban} text="Project pipeline (Kanban)" />
                            <Feature icon={Clock} text="Timesheets & approvals" />
                            <Feature icon={CalendarBlank} text="Crew schedules & calendar" />
                            <Feature icon={ChartLineUp} text="Live job P&L & margin" />
                            <Feature icon={WifiSlash} text="100% offline-first" />
                        </div>
                        <div className="grid grid-cols-3 gap-3 pt-6 border-t border-white/10">
                            <Stat value="<30m" label="Per estimate" />
                            <Stat value="±15%" label="ROM confidence" />
                            <Stat value="100%" label="Offline" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Right — form */}
            <div className="flex flex-col items-center justify-center p-6 lg:p-12 bg-[var(--rom-bg)]">
                <div className="w-full max-w-md">
                    <h1 className="font-display font-black text-3xl mb-1">Sign in</h1>
                    <p className="text-[var(--rom-text-2)] mb-8">
                        Use your {FRANCHISE_NAME} credentials.
                    </p>

                    {!online && (
                        <div className="mb-4 p-4 rounded-[4px] bg-amber-50 border-2 border-amber-300 text-amber-900" data-testid="offline-banner">
                            <div className="flex items-center gap-2 font-bold text-sm mb-1.5">
                                <WifiSlash size={18} weight="bold" />
                                You&apos;re offline
                            </div>
                            <div className="text-xs leading-relaxed">
                                The <b>first sign-in on this device must happen online</b>. Connect to the internet, sign in once,
                                then you can use the app fully offline going forward.
                            </div>
                        </div>
                    )}

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="label">Email</label>
                            <div className="relative">
                                <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                                <input
                                    type="email"
                                    className="input pl-9"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@servpro.app"
                                    autoComplete="email"
                                    required
                                    data-testid="login-email"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="label">Password</label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                                <input
                                    type="password"
                                    className="input pl-9"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    required
                                    data-testid="login-password"
                                />
                            </div>
                        </div>
                        {err && (
                            <div
                                className="text-sm text-[var(--rom-danger)] bg-red-50 border border-red-200 px-3 py-2 rounded-[4px]"
                                data-testid="login-error"
                            >
                                {err}
                            </div>
                        )}
                        <button
                            type="submit"
                            className="btn btn-primary w-full justify-center"
                            disabled={busy}
                            data-testid="login-submit"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                            <ArrowRight size={14} weight="bold" />
                        </button>
                    </form>

                    <div className="mt-10 pt-6 border-t border-[var(--rom-border)]">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rom-text-3)] mb-3 font-semibold">
                            Demo accounts
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {DEMO_USERS.map((u) => (
                                <button
                                    key={u.email}
                                    type="button"
                                    onClick={() => fill(u)}
                                    data-testid={`demo-${u.role.toLowerCase()}`}
                                    className="text-left p-3 border border-[var(--rom-border)] rounded-[4px] hover:border-[var(--rom-navy)] transition-colors bg-white"
                                >
                                    <div className="font-display font-bold text-xs uppercase tracking-wider">
                                        {u.role}
                                    </div>
                                    <div className="text-[11px] text-[var(--rom-text-2)] font-mono mt-1 truncate">
                                        {u.email}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Stat({ value, label }) {
    return (
        <div>
            <div className="font-display font-black text-2xl text-[var(--rom-orange)]">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/80 mt-1">{label}</div>
        </div>
    );
}

function Feature({ icon: Icon, text }) {
    return (
        <div className="flex items-center gap-2.5 text-white/90">
            <span className="w-7 h-7 rounded-[5px] bg-white/10 flex items-center justify-center shrink-0">
                <Icon size={15} weight="bold" className="text-[var(--rom-orange)]" />
            </span>
            <span className="text-sm">{text}</span>
        </div>
    );
}
